/**
 * Maps analytics / data-model API source keys to `jep-service-icon` `service` slugs.
 * Shared by Data Model table and analytics chart field pickers — keep in sync with
 * `jep-service-icon.component.html` and framework `analytics-service-icon-assets.ts` (SVG paths).
 */
export interface DataSourceIconMeta {
	service: string;
	label: string;
}

export const DATA_SOURCE_ICON_META: Record<string, DataSourceIconMeta> = {
	googleAds: { service: 'google-ads', label: 'Google Ads' },
	googleAnalytics: { service: 'google-analytics', label: 'Google Analytics' },
	googleBusinessProfile: { service: 'google-my-business', label: 'Google Business Profile' },
	googleSearchConsole: { service: 'google-search-console', label: 'Google Search Console' },
	facebookAds: { service: 'facebook-ads', label: 'Facebook Ads' },
	microsoftAds: { service: 'microsoft-ads', label: 'Microsoft Ads' },
	twitterAds: { service: 'twitter-ads', label: 'Twitter Ads' },
	tiktokAds: { service: 'tiktok-ads', label: 'TikTok Ads' },
	instagramAds: { service: 'instagram-ads', label: 'Instagram Ads' },
	linkedInAds: { service: 'linkedin-ads', label: 'LinkedIn Ads' },
	bing: { service: 'bing', label: 'Bing' },
	/** Must match `*ngSwitchCase="'google-analytics4'"` in jep-service-icon template. */
	googleAnalytics4: {
		service: 'google-analytics4',
		label: 'Google Analytics 4',
	},
	custom: { service: 'ai', label: 'Custom' },
	unified: { service: 'jepto', label: 'Unified' },
	/** External analytical BigQuery (JEP-10218). */
	bigquery: { service: 'google-big-query', label: 'BigQuery' },
	/** External analytical Google Sheets (JEP-10218). */
	googleSheets: { service: 'google-sheets', label: 'Google Sheets' },
	/** Native Xero data source (JEP-10363 / JEP-10400). */
	xero: { service: 'xero', label: 'Xero' },
	/** AI Visibility warehouse cubes (`aiVisibility.*` schema group). */
	aiVisibility: {
		service: 'ai-visibility',
		label: 'AI Visibility',
	},
};

/** Kebab / alias / noisy-casing inputs from APIs → keys of `DATA_SOURCE_ICON_META`. */
const CONNECTOR_NAME_ALIASES: Record<string, keyof typeof DATA_SOURCE_ICON_META> = {
	'google-ads': 'googleAds',
	googleads: 'googleAds',
	'google-analytics': 'googleAnalytics',
	'google-analytics4': 'googleAnalytics4',
	'google-analytics-4': 'googleAnalytics4',
	ga4: 'googleAnalytics4',
	'google-search-console': 'googleSearchConsole',
	'googe-search-console': 'googleSearchConsole',
	'google-my-business': 'googleBusinessProfile',
	'google-business-profile': 'googleBusinessProfile',
	'facebook-ads': 'facebookAds',
	facebookads: 'facebookAds',
	facebook: 'facebookAds',
	'tiktok-ads': 'tiktokAds',
	tiktok: 'tiktokAds',
	'linkedin-ads': 'linkedInAds',
	linkedin: 'linkedInAds',
	'microsoft-ads': 'microsoftAds',
	'twitter-ads': 'twitterAds',
	'instagram-ads': 'instagramAds',
	bing: 'bing',
	ai: 'custom',
	unified: 'unified',
	bigquery: 'bigquery',
	'google-big-query': 'bigquery',
	'google-bigquery': 'bigquery',
	googlesheets: 'googleSheets',
	'google-sheets': 'googleSheets',
	sheets: 'googleSheets',
	xero: 'xero',
	'ai-visibility': 'aiVisibility',
	aivisibility: 'aiVisibility',
	ai_visibility: 'aiVisibility',
};

function screamingSnakeToCamelConnectorId(s: string): string {
	const parts = s.split('_').filter(Boolean);
	if (parts.length === 0) {
		return s;
	}
	return (
		parts[0].toLowerCase() +
		parts
			.slice(1)
			.map(
				p => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase()
			)
			.join('')
	);
}

/**
 * Maps arbitrary API spellings (GraphQL enums, kebab slugs, PascalCase, SCREAMING_SNAKE)
 * to a key present in `DATA_SOURCE_ICON_META`.
 */
export function canonicalDataSourceMetaKey(raw: string): string | undefined {
	const t = raw.trim();
	if (!t) {
		return undefined;
	}
	if (DATA_SOURCE_ICON_META[t]) {
		return t;
	}
	const lowerFirst = t.charAt(0).toLowerCase() + t.slice(1);
	if (lowerFirst !== t && DATA_SOURCE_ICON_META[lowerFirst]) {
		return lowerFirst;
	}
	const aliasKey = t.toLowerCase().replace(/_/g, '-');
	const fromAlias = CONNECTOR_NAME_ALIASES[aliasKey];
	if (fromAlias && DATA_SOURCE_ICON_META[fromAlias]) {
		return fromAlias;
	}
	if (/^[A-Z0-9_]+$/.test(t) && t.includes('_')) {
		const camel = screamingSnakeToCamelConnectorId(t);
		if (DATA_SOURCE_ICON_META[camel]) {
			return camel;
		}
	}
	return undefined;
}

/** Card `service` key for `jep-data-source-card` icons and footers. */
export function dataSourceCardServiceKey(item: {
	service?: string | null;
	type?: string | null;
	externalDataSource?: boolean;
}): string {
	const raw = (item.service || item.type || '').trim();
	const canonical = canonicalDataSourceMetaKey(raw);
	if (canonical) {
		return canonical;
	}
	if (item.externalDataSource) {
		if (raw === 'googleCloudPlatform') {
			return 'bigquery';
		}
		if (raw === 'googleDrive') {
			return 'googleSheets';
		}
	}
	return raw;
}

export function resolveDataSourceIconEntries(
	names: string[]
): DataSourceIconMeta[] {
	return names
		.map(n => {
			const key = canonicalDataSourceMetaKey(n);
			return key ? DATA_SOURCE_ICON_META[key] : undefined;
		})
		.filter((m): m is DataSourceIconMeta => !!m);
}

/**
 * Resolves a single `jep-service-icon` / analytics SVG slug from schema field
 * `dataSource` only. Missing / unknown `dataSource` → no icon (blank).
 */
export function primaryServiceSlugForSchemaField(field: {
	dataSources?: string[];
	sourceGroup?: string;
	name?: string;
	dataSource?: string | null;
}): string | undefined {
	const dataSource = field.dataSource?.trim();
	if (!dataSource) {
		return undefined;
	}
	const mk = canonicalDataSourceMetaKey(dataSource);
	return mk ? DATA_SOURCE_ICON_META[mk].service : undefined;
}
